import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import PageContainer from "../components/layout/PageContainer";
import Card from "../components/common/Card";
import Button from "../components/common/Button";

const ScreenshotPlaceholder = ({ label }) => (
  <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border-light bg-bg-light py-10 px-4 text-center">
    <Icon icon="mdi:image-outline" className="text-3xl text-text-secondary" />
    <p className="text-xs text-text-secondary">
      Screenshot: <span className="font-medium">{label}</span>
    </p>
  </div>
);

const GuideStep = ({ number, title, children, screenshotLabel }) => (
  <div className="flex gap-4 md:gap-5">
    <div className="flex flex-col items-center">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-primary text-white font-semibold text-sm shadow-sm">
        {number}
      </div>
      <div className="w-px flex-1 bg-border-light mt-2 last:hidden" />
    </div>
    <div className="pb-8 flex-1">
      <h3 className="text-base md:text-lg font-semibold text-text-dark">
        {title}
      </h3>
      <div className="text-sm text-text-secondary mt-1 space-y-2">
        {children}
      </div>
      {screenshotLabel && <ScreenshotPlaceholder label={screenshotLabel} />}
    </div>
  </div>
);

const WordPressGuide = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <button
        type="button"
        onClick={() => navigate("/integrations")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        <Icon icon="mdi:arrow-left" className="text-lg" />
        Back to Integrations
      </button>

      <div className="mb-6 flex items-center gap-4">
        <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm border border-border-light">
          <Icon icon="mdi:wordpress" className="text-4xl text-[#21759b]" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-text-dark">
            How to Use the WordPress Plugin
          </h1>
          <p className="text-sm md:text-base text-text-secondary mt-1">
            A step-by-step walkthrough to install the ADA Compliance Scanner
            plugin and connect your WordPress site to SentryPrime.
          </p>
        </div>
      </div>

      <Card>
        <div className="mb-6 p-4 bg-info/10 border border-info/20 rounded-lg flex gap-3">
          <Icon
            icon="mdi:information-outline"
            className="text-xl text-info shrink-0 mt-0.5"
          />
          <p className="text-sm text-text-secondary">
            You only need to do this once per WordPress site. Once connected,
            you can scan pages, review violations, and apply fixes directly
            from the <strong>Integrations</strong> page.
          </p>
        </div>

        <GuideStep number={1} title="Download the plugin">
          <p>
            Our plugin isn't listed on the WordPress.org plugin directory yet,
            so download it directly from SentryPrime first.
          </p>
          <a
            href="https://webaccessibility-bucket01.s3.us-east-1.amazonaws.com/wordpress/ada-compliance-scanners.zip"
            download
            className="inline-flex items-center gap-2 mt-2 bg-gradient-to-r from-indigo-600 to-primary text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:-translate-y-0.5 transition-transform"
          >
            <Icon icon="mdi:download" className="text-lg" />
            Download Plugin (.zip)
          </a>
        </GuideStep>

        <GuideStep
          number={2}
          title="Upload & install the plugin in WordPress"
          screenshotLabel="WP Admin → Plugins → Add New → Upload Plugin"
        >
          <p>
            In your WordPress dashboard, go to{" "}
            <strong>Plugins → Add New → Upload Plugin</strong> and choose the{" "}
            <code className="px-1 py-0.5 bg-bg-light rounded text-xs">
              ada-compliance-scanner.zip
            </code>{" "}
            file you just downloaded.
          </p>
          <p>
            Click <strong>Install Now</strong>, then click{" "}
            <strong>Activate Plugin</strong>.
          </p>
        </GuideStep>

        <GuideStep
          number={3}
          title="Generate an API key"
          screenshotLabel="WP Admin → ADA Scanner → API Settings"
        >
          <p>
            In the WordPress sidebar, go to{" "}
            <strong>ADA Scanner → API Settings</strong>.
          </p>
          <p>
            Click <strong>Generate New API Key</strong> and copy the key
            (it starts with{" "}
            <code className="px-1 py-0.5 bg-bg-light rounded text-xs">
              ada_
            </code>
            ). You'll paste this into SentryPrime in the next step, so keep
            this tab open or save it somewhere safe.
          </p>
        </GuideStep>

        <GuideStep
          number={4}
          title="Connect your site in SentryPrime"
          screenshotLabel="Website Connections → Add Website form"
        >
          <p>
            In SentryPrime, go to{" "}
            <button
              onClick={() => navigate("/website-connections")}
              className="text-primary hover:underline font-medium"
            >
              Website Connections
            </button>{" "}
            and click <strong>Add Website</strong>.
          </p>
          <p>
            Select <strong>WordPress</strong> as the platform, then fill in:
          </p>
          <ul className="list-disc list-inside pl-1 space-y-1">
            <li>
              <strong>Site Name</strong> — any label to identify this site
            </li>
            <li>
              <strong>Site URL</strong> — your full WordPress site address
              (e.g. https://yoursite.com)
            </li>
            <li>
              <strong>API Key</strong> — the key you copied in Step 3
            </li>
          </ul>
          <p>
            Click <strong>Save</strong> to create the connection.
          </p>
        </GuideStep>

        <GuideStep
          number={5}
          title="Test the connection"
          screenshotLabel="Integrations page → Test Connection button"
        >
          <p>
            Head back to the{" "}
            <button
              onClick={() => navigate("/integrations")}
              className="text-primary hover:underline font-medium"
            >
              Integrations
            </button>{" "}
            page, select your newly connected site, and click{" "}
            <strong>Test Connection</strong>.
          </p>
          <p>
            You should see a "Connection test successful!" message. If it
            fails, double check the Site URL and API Key from Step 3 and 4.
          </p>
        </GuideStep>

        <GuideStep
          number={6}
          title="Scan a page for accessibility issues"
          screenshotLabel="Select a page → Scan Page Accessibility"
        >
          <p>
            With your site selected, pick a page from the page list and click{" "}
            <strong>Scan Page Accessibility</strong>. SentryPrime will scan
            the live page and show every WCAG violation it finds.
          </p>
        </GuideStep>

        <GuideStep
          number={7}
          title="Apply AI fixes (or roll them back)"
          screenshotLabel="Scan results modal → Get AI Fixes"
        >
          <p>
            From the scan results, click <strong>Get AI Fixes</strong> to
            automatically generate and apply fixes to the violations found.
          </p>
          <p>
            Not happy with a change? Use{" "}
            <strong>Rollback Fixes</strong> on the connection card to restore
            the original HTML at any time.
          </p>
        </GuideStep>

        <div className="mt-2 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
          <Icon
            icon="mdi:check-circle-outline"
            className="text-xl text-success shrink-0 mt-0.5"
          />
          <p className="text-sm text-text-secondary">
            That's it — your WordPress site is fully connected. You can repeat
            this process to connect additional sites at any time.
          </p>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            variant="primary"
            icon="mdi:arrow-right"
            iconPosition="right"
            onClick={() => navigate("/integrations")}
          >
            Go to Integrations
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
};

export default WordPressGuide;
